/**
 * MappingDrawer — first-upload column mapping screen.
 *
 * UX (per user spec): Option A layout — driver is the client's column, the
 * target is a dropdown of canonical fields. Suggestions populate the
 * dropdown automatically (when the AI proposes a match); the user can
 * accept, change, or skip each row. Confirmed mappings persist to
 * reporter_customer_mappings.
 *
 * Flow:
 *   1. User picks a CSV/Excel file (census).
 *   2. We read the header row, send headers to /api/reporter/mappings/propose.
 *   3. We render rows: each `Your column` next to a `[canonical field ▼]` dropdown.
 *   4. User confirms or edits each row, clicks Save.
 *   5. We POST every confirmed pair to /api/reporter/mappings.
 *   6. Drawer closes; caller knows mapping is ready.
 *
 * SOC 2 surface: this UI only sends column NAMES (not data values) to the
 * AI proposal endpoint. No PHI leaves the browser via this drawer.
 */

import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

function readCsvHeaders(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.onload = () => {
      const text = String(reader.result || '');
      const firstLine = text.split(/\r\n|\r|\n/)[0] || '';
      // naive CSV split — handles common quoting case
      const cells = firstLine.match(/("([^"]|"")*"|[^,]+)/g) || [];
      const cleaned = cells.map(c => c.replace(/^"(.*)"$/, '$1').replace(/""/g, '"').trim()).filter(Boolean);
      resolve(cleaned);
    };
    reader.readAsText(file);
  });
}

export default function MappingDrawer({ measureId, measureLabel, onClose }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'mapping' | 'saving' | 'done'
  const [headers, setHeaders] = useState([]);
  const [canonicalFields, setCanonicalFields] = useState([]);
  const [rows, setRows] = useState([]); // { source_column, canonical_field, confidence, rationale }
  const [missing, setMissing] = useState([]);
  const [error, setError] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    try {
      const hdrs = await readCsvHeaders(file);
      if (!hdrs.length) throw new Error("Couldn't find a header row in that file.");
      setHeaders(hdrs);
      const res = await fetch(`${API}/api/reporter/mappings/propose`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ measureId, customHeaders: hdrs }),
      });
      if (!res.ok) throw new Error((await res.json()).error || `Propose failed (${res.status})`);
      const data = await res.json();
      setCanonicalFields(data.canonical_fields || []);
      setRows(data.suggestions || []);
      setMissing(data.missing_canonical_fields || []);
      setStep('mapping');
    } catch (e) {
      setError(e.message);
    }
  };

  const updateRow = (idx, canonical_field) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, canonical_field, confidence: 1.0, rationale: 'user-confirmed' } : r));
  };

  const save = async () => {
    setStep('saving');
    setSavedCount(0);
    const toSave = rows.filter(r => r.canonical_field);
    let saved = 0;
    for (const r of toSave) {
      try {
        const res = await fetch(`${API}/api/reporter/mappings`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            measureId,
            sourceColumn: r.source_column,
            canonicalField: r.canonical_field,
            source: r.rationale === 'previously confirmed by this organization' ? 'manual' : 'copilot_confirmed',
            confidence: r.confidence,
          }),
        });
        if (res.ok) saved++;
      } catch { /* keep going */ }
      setSavedCount(saved);
    }
    setStep('done');
  };

  const remainingMissing = missing.filter(m => !rows.some(r => r.canonical_field === m));

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/40">
      <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-700">Map your columns</div>
            <h2 className="text-lg font-semibold text-slate-900">{measureLabel || measureId}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === 'pick' && (
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                Upload your records file (CSV or Excel-exported CSV). We only read the column names —
                no row values leave your browser at this step.
              </p>
              <label className="block border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-red-700">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className="text-sm text-slate-700 font-medium">Choose a CSV file</div>
                <div className="text-xs text-slate-500 mt-1">First row should be the column header.</div>
              </label>
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</div>}
            </div>
          )}

          {step === 'mapping' && (
            <div className="p-6 space-y-4">
              <div className="text-sm text-slate-700">
                <span className="font-semibold">{headers.length}</span> columns detected.
                Confirm the suggestions or pick the right field for each one.
              </div>
              <div className="border border-slate-200 rounded overflow-hidden">
                <div className="grid grid-cols-[1fr_24px_1fr_auto] gap-3 px-3 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-mono">
                  <div>Your column</div><div></div><div>What we&apos;ll use it for</div><div>Confidence</div>
                </div>
                {rows.map((r, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_24px_1fr_auto] gap-3 px-3 py-2 border-t border-slate-200 items-center">
                    <div className="text-sm text-slate-900 font-mono truncate" title={r.source_column}>{r.source_column}</div>
                    <div className="text-slate-400 text-center">➜</div>
                    <select
                      value={r.canonical_field || ''}
                      onChange={(e) => updateRow(idx, e.target.value || null)}
                      className={`text-sm border rounded px-2 py-1 w-full ${r.canonical_field ? 'border-slate-300' : 'border-amber-300 bg-amber-50'}`}
                    >
                      <option value="">— don&apos;t use this column —</option>
                      {canonicalFields.map((c) => (
                        <option key={c.canonical_field} value={c.canonical_field}>
                          {c.element ? `${c.element} — ${c.canonical_field}` : c.canonical_field}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs font-mono text-slate-500 tabular-nums w-12 text-right">
                      {r.canonical_field ? (r.confidence >= 0.99 ? '100%' : Math.round(r.confidence * 100) + '%') : '—'}
                    </div>
                  </div>
                ))}
              </div>

              {remainingMissing.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded p-3 text-xs">
                  <div className="font-semibold text-amber-800 mb-1">Still missing ({remainingMissing.length}):</div>
                  <div className="text-amber-700 leading-snug">
                    These fields don&apos;t have a mapping yet. The count engine needs each of them.
                    You may need to add the column to your export — or pick a different file.
                  </div>
                  <ul className="mt-2 list-disc list-inside text-amber-900 font-mono text-[11px]">
                    {remainingMissing.map(f => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {step === 'saving' && (
            <div className="p-8 text-center text-sm text-slate-700">
              Saving mappings… {savedCount}
            </div>
          )}

          {step === 'done' && (
            <div className="p-8 text-center space-y-3">
              <div className="text-2xl">✓</div>
              <div className="text-sm text-slate-900 font-semibold">
                Saved {savedCount} mapping{savedCount === 1 ? '' : 's'}.
              </div>
              <div className="text-xs text-slate-500">Future uploads of this file will use these mappings automatically.</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-end gap-2 bg-slate-50">
          {step === 'mapping' && (
            <>
              <button onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                onClick={save}
                disabled={!rows.some(r => r.canonical_field)}
                className="text-sm px-4 py-1.5 bg-red-700 text-white rounded hover:bg-red-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                Save mappings
              </button>
            </>
          )}
          {step === 'done' && (
            <button onClick={onClose} className="text-sm px-4 py-1.5 bg-slate-900 text-white rounded hover:bg-slate-800">Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
